<template>
  <div>
    <div
      v-if="hasUpdateSettingAccess"
      class="row controls"
    >
      <button
        type="button"
        class="btn btn--darkblue"
        @click="openAddSiteModal()"
      >{{ $t("action.add") }}</button>
      <button
        v-if="!editView"
        type="button"
        class="btn btn--orange"
        @click="editView = true"
      >{{ $t('action.edit') }}</button>
      <button
        v-if="editView"
        v-hkldreset
        type="button"
        class="btn btn--darkblue"
        @click="discardChange()"
      >{{ $t('action.cancel') }}</button>
      <button
        v-if="editView"
        v-hkldreset
        type="button"
        class="btn btn--orange"
        @click="save()"
      >{{ $t('action.save') }}</button>
    </div>
    <div
      id="setting-locations"
      class="table-wrapper"
    >
      <table class="setting-table">
        <thead>
          <th>{{ $t(itemHeaders[0].name) }}</th>
          <th
            v-for="(header, index) in itemHeaders"
            v-show="Math.ceil(index/headerPageSize) === headerPageNum"
            :key="index"
            :class="header.class"
            @click="navToDetail(header.id)"
          >
            <a>
              {{ header.name }} {{ orderbyAttr === header.code ? reverse ? '&#9660;' : '&#9650;' : null }}
              <i
                v-if="header.code != 'username'"
                class="fa fa-external-link"
                aria-hidden="true"
              />
            </a>
          </th>
          <th style="min-width: 120px;">
            <button
              type="button"
              class="btn icon right"
              :disabled="Math.ceil((itemHeaders.length - 1)/headerPageSize) <= headerPageNum"
              @click="headerPageNum += 1"
            >&#9654;</button>
            <button
              type="button"
              class="btn icon right"
              :disabled="headerPageNum<=1"
              @click="headerPageNum -= 1"
            >&#9664;</button>
          </th>
        </thead>
        <tbody>
          <tr>
            <td>
              <input
                v-model="itemHeaders[0].filter"
                type="text"
                @input="clearIndex()"
              >
            </td>
            <td colspan="42" />
          </tr>
          <tr
            v-for="(item, index) in orderedItems"
            :key="index"
          >
            <td>
              <a @click="navToUserDetail(item.id)">{{ userName(item) }}</a>
            </td>
            <td
              v-for="(header, index) in itemHeaders"
              v-show="index > 0 && Math.ceil(index/headerPageSize) === headerPageNum"
              :key="index"
              :title="header.hasDepartment ? null : $t('hint.siteNotBelongToAnyDepartment')"
            >
              <input
                v-if="editView || item.locations[header.code]"
                id="checkBox"
                v-model="item.locations[header.code]"
                v-hkldinput
                type="checkbox"
                :disabled="!editView || !header.hasDepartment"
                @change="updateUserRelationship(item.id, header, item.locations[header.code])"
              >
            </td>
            <td />
          </tr>
        </tbody>
      </table>
    </div>
    <AddSiteModal ref="addSiteModal" />
  </div>
</template>

<script>
import _ from 'lodash'
import AddSiteModal from './AddSiteModal'
import { aclHelper } from '@/utils/aclHelper'
import userHelper from '@/utils/userHelper'
import { ROUTER_PATHS } from '@/constants/router'
import { mapActions, mapGetters } from 'vuex'
import { updateUserLocations } from '@/api/locations/request'
import { getUsersInSites, getSitesDepartments } from '@/api/locations/site.request'

function getDefaultData () {
  return {
    loadingTenderTable: false,
    itemHeaders: [
      {
        code: 'username',
        name: 'settings.hklUser',
        filter: '',
      },
    ],
    items: [],
    pendingUserLoc: {},
    pendingUserDepartment: {},
    // non persistency values
    orderbyAttr: 'updateDate',
    reverse: 0,
    editView: false,
    headerPageNum: 1,
  }
}

export default {
  components: {
    AddSiteModal,
  },
  data () {
    return {
      ...getDefaultData(),
      headerPageSize: 5,
    }
  },

  computed: {
    ...mapGetters('user', ['currentUser']),
    orderedItems () {
      const filteredResult =
        _.filter(this.items, item => {
          if ((item.username.toString().split('@')[0].toLowerCase().indexOf(this.itemHeaders[0].filter.toLowerCase()) !== -1) ||
            (item.displayName || ' ').toString().toLowerCase().indexOf(this.itemHeaders[0].filter.toLowerCase()) !== -1) {
            return item
          }
        })
      const result = _.orderBy(filteredResult, 'displayName')
      return this.reverse ? result.reverse() : result
    },
    hasUpdateSettingAccess () {
      return aclHelper.getSettingsUpdateAccess()
    },
  },
  beforeDestroy: function () {
    window.removeEventListener('resize', this.handleResize)
  },
  async mounted () {
    await this.initPage()
    window.addEventListener('resize', this.handleResize)
    this.handleResize()
  },
  methods: {
    ...mapActions('user', ['reloadUser']),
    sortBy (attr) {
      if (this.orderbyAttr === attr) {
        this.reverse = !this.reverse
      }
      this.orderbyAttr = attr
    },
    initHeader (locations) {
      const defaultData = getDefaultData()
      this.itemHeaders = locations.reduce((headerDict, location) => {
        const departmentId = _.get(location, 'departments.id', null)
        const hasDepartment = !!departmentId
        headerDict.push({
          code: location.code,
          name: location.code,
          id: location.id,
          hasDepartment,
          departmentId,
        })
        return headerDict
      }, defaultData.itemHeaders)
    },
    navToDetail (id) {
      if (id) {
        this.navPage(ROUTER_PATHS.SITE_PROFILE, id)
      }
    },
    userName (user) {
      if (user.username) {
        const name = userHelper.getUserName(user.username, user.displayName)
        return name
      }
    },
    navToUserDetail (id) {
      if (id) {
        this.navPage('hkl-user-detail', id)
      }
    },
    navPage (name, id) {
      this.$router.push({
        name,
        params: {
          id,
        },
      })
    },

    clearIndex () {
      this.tablePageNum = 1
    },
    openAddSiteModal () {
      this.$refs.addSiteModal.open()
    },
    async initPage () {
      try {
        const users = await getUsersInSites()
        users.forEach((user) => {
          const locations = {}
          user.locations.reduce((locationsDict, location) => {
            locationsDict[location.code] = true
            return locationsDict
          }, locations)
          user.locations = locations
        }, this)
        const locations = await await getSitesDepartments()
        const orderedLocations = _.orderBy(locations, 'code')
        this.initHeader(orderedLocations)
        this.items = users
      } catch (error) {
        console.error(error)
      }
    },
    discardChange () {
      this.editView = false
      this.initPage()
    },
    async save () {
      try {
        await updateUserLocations(this.pendingUserLoc)
        await this.reloadUser({ adId: this.currentUser.AD_id })
        this.editView = false
        this.initPage()
      } catch (err) {
        console.error(err)
      }
    },
    updateUserRelationship (userId, location, val) {
      const locId = location.id
      if (!this.pendingUserLoc[userId]) {
        this.pendingUserLoc[userId] = {}
      }
      this.pendingUserLoc[userId][locId] = val
    },
    handleResize () {
      const tableWidth = document.getElementById('setting-locations').offsetWidth
      this.headerPageSize = (tableWidth > 1024) ? Math.floor((tableWidth - 270) / 132) : 5
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/settings/style';
.table-wrapper {
  padding: 0 24px 15px 24px;
}
.controls {
  margin-right: 24px;
}
</style>
